import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, MapPin, Truck, CheckCircle2, Cookie as CookieIcon, Package, Clock } from "lucide-react";
import Layout from "../components/Layout";
import CookieImage from "../components/CookieImage";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const STATUS_META = {
  confirmed:  { label: "Confirmed",  icon: CheckCircle2, color: "text-blue-700 bg-blue-50 border-blue-200" },
  preparing:  { label: "Preparing",  icon: CookieIcon,   color: "text-amber-700 bg-amber-50 border-amber-200" },
  ready:      { label: "Ready",      icon: Package,      color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  completed:  { label: "Completed",  icon: CheckCircle2, color: "text-gray-600 bg-gray-100 border-gray-200" },
  cancelled:  { label: "Cancelled",  icon: Clock,        color: "text-red-700 bg-red-50 border-red-200" },
};

const STEPS = ["confirmed", "preparing", "ready", "completed"];

function StatusTimeline({ status }) {
  const isCancelled = status === "cancelled";
  if (isCancelled) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
        <p className="font-bold text-red-700 text-sm uppercase tracking-wider">Cancelled</p>
        <p className="text-xs text-red-600/80 mt-1">If this wasn't expected, please contact us.</p>
      </div>
    );
  }
  const currentIdx = STEPS.indexOf(status);
  return (
    <div className="flex items-center justify-between gap-1" data-testid="order-status-timeline">
      {STEPS.map((s, i) => {
        const reached = i <= currentIdx;
        const meta = STATUS_META[s];
        const Icon = meta.icon;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center flex-shrink-0">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${reached ? "bg-[#FF1F87] text-white" : "bg-gray-100 text-gray-400"}`}>
                <Icon className="w-4 h-4" />
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-bold mt-1.5 ${reached ? "text-[#FF1F87]" : "text-gray-400"}`}>{meta.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-1 rounded-full mb-5 ${i < currentIdx ? "bg-[#FF1F87]" : "bg-gray-200"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function OrderReceiptPage() {
  const { orderNumber } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const load = () => api.get(`/orders/${orderNumber}`)
      .then(r => { if (!stopped) setOrder(r.data); })
      .catch(e => { if (!stopped) setErr(e?.response?.data?.detail || "Order not found"); });
    load();
    const t = setInterval(load, 15000);
    return () => { stopped = true; clearInterval(t); };
  }, [orderNumber, user]);

  if (!user) {
    return (
      <Layout>
        <section className="min-h-[60vh] flex items-center justify-center px-6">
          <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md text-center">
            <h2 className="font-display font-extrabold text-2xl">Sign in to view this order</h2>
            <Link to="/login" className="btn-pink mt-5 inline-block">LOG IN</Link>
          </div>
        </section>
      </Layout>
    );
  }

  if (err) {
    return (
      <Layout>
        <section className="min-h-[60vh] flex items-center justify-center px-6">
          <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md text-center">
            <h2 className="font-display font-extrabold text-2xl">{err}</h2>
            <Link to="/orders" className="btn-pink mt-5 inline-block">Back to your orders</Link>
          </div>
        </section>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-white" />
        </div>
      </Layout>
    );
  }

  const meta = STATUS_META[order.status] || STATUS_META.confirmed;
  const dateOrdered = order.paid_at || order.created_at;
  const boxLabel = order.box_size === 1 ? "1 Cookie" : `${order.box_size} Cookie Box`;

  return (
    <Layout>
      <section className="px-4 sm:px-6 py-8 sm:py-12 max-w-2xl mx-auto" data-testid="order-receipt">
        <Link to="/orders" className="inline-flex items-center gap-1 text-white/90 hover:text-white text-sm font-bold mb-4" data-testid="back-to-orders">
          <ArrowLeft className="w-4 h-4" /> Back to orders
        </Link>

        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-gray-500 font-bold">Order</p>
              <h1 className="font-display font-extrabold text-2xl sm:text-3xl" data-testid="receipt-order-number">#{order.order_number}</h1>
              <p className="text-xs text-gray-500 mt-1">Placed {new Date(dateOrdered).toLocaleString()}</p>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[11px] font-bold uppercase tracking-wider ${meta.color}`} data-testid={`receipt-status-${order.status}`}>
              <meta.icon className="w-3 h-3" /> {meta.label}
            </span>
          </div>

          <div className="mt-6">
            <StatusTimeline status={order.status} />
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
            <div className="bg-[#FFF7F1] rounded-2xl p-3">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Method</p>
              <p className="font-bold mt-1 flex items-center gap-1">
                {order.method === "delivery" ? <><Truck className="w-4 h-4 text-[#FF1F87]" /> Delivery</> : <><MapPin className="w-4 h-4 text-[#FF1F87]" /> Pickup</>}
              </p>
            </div>
            <div className="bg-[#FFF7F1] rounded-2xl p-3">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Box Size</p>
              <p className="font-bold mt-1">{boxLabel}</p>
            </div>
            {order.pickup_date ? (
              <div className="bg-[#FFF7F1] rounded-2xl p-3 col-span-2">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{order.method === "delivery" ? "Delivery Date" : "Pickup Date"}</p>
                <p className="font-bold mt-1">{order.pickup_date}</p>
              </div>
            ) : null}
            {order.method === "delivery" && order.address ? (
              <div className="bg-[#FFF7F1] rounded-2xl p-3 col-span-2">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Delivery Address</p>
                <p className="font-bold mt-1 leading-snug">
                  {order.address.street}<br />
                  {order.address.city}, {order.address.state || "SC"} {order.address.zip}
                </p>
              </div>
            ) : null}
          </div>

          <div className="mt-6">
            <p className="text-[11px] uppercase tracking-widest text-gray-500 font-bold mb-2">Flavors</p>
            <div className="space-y-2">
              {(order.flavor_lines || []).map(fl => (
                <div key={fl.flavor_id} className="flex items-center gap-3 bg-[#FFE4EE]/40 rounded-2xl p-3" data-testid={`receipt-flavor-${fl.flavor_id}`}>
                  <CookieImage flavorKey={fl.image_key} imageUrl={fl.image_url} className="w-12 h-12 rounded-full flex-shrink-0" />
                  <p className="font-bold flex-1 min-w-0 truncate">{fl.name}</p>
                  <p className="font-display font-extrabold text-[#FF1F87]">× {fl.quantity}</p>
                </div>
              ))}
              {(!order.flavor_lines || order.flavor_lines.length === 0) && (
                <p className="text-xs text-gray-500">No flavor details available.</p>
              )}
            </div>
          </div>

          <div className="mt-6 border-t border-gray-100 pt-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="font-bold">${(order.subtotal || 0).toFixed(2)}</span></div>
            {order.delivery_fee ? <div className="flex justify-between"><span className="text-gray-500">Delivery Fee</span><span className="font-bold">${order.delivery_fee.toFixed(2)}</span></div> : null}
            {order.tip ? <div className="flex justify-between"><span className="text-gray-500">Tip</span><span className="font-bold">${order.tip.toFixed(2)}</span></div> : null}
            {order.discount ? <div className="flex justify-between text-emerald-700"><span>Rewards (free cookie)</span><span className="font-bold">−${order.discount.toFixed(2)}</span></div> : null}
            <div className="flex justify-between pt-2 border-t border-gray-100 mt-2">
              <span className="font-display font-extrabold text-lg">Total Paid</span>
              <span className="font-display font-extrabold text-lg text-[#FF1F87]" data-testid="receipt-total">${(order.total || 0).toFixed(2)}</span>
            </div>
          </div>

          <p className="text-[10px] text-gray-400 mt-5 text-center">Status updates auto-refresh every 15 seconds.</p>
        </div>
      </section>
    </Layout>
  );
}
